/**
 * MSPBPatientDetailView Component
 * 
 * MSPB-specific patient detail showing:
 * - Episode details with LOS and disposition
 * - Episode cost vs. national average
 * - Phase breakdown with pie chart
 * - Service category breakdown
 * - Post-discharge breakdown
 * - Other measures for this patient
 * 
 * @see MIPS_Cost_Dashboard_Implementation_Spec_v2_2, Phase 4
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowLeft,
  User,
  Calendar,
  Building2,
  ChevronRight,
  AlertTriangle,
  Bed,
  Home,
  ArrowRight,
  Clock,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';

// ============================================
// Types
// ============================================

interface MSPBPatientDetailProps {
  mbi: string;
  practiceId: string;
  npi?: string;
  onBack: () => void;
  onNavigateToMeasure?: (measureId: string, mbi: string) => void;
}

interface MSPBPatientData {
  mbi: string;
  episodeId: string;
  demographics: {
    dateOfBirth: string;
    gender: string;
    riskPercentile: number;
  };
  episodeDetails: {
    admissionDate: string;
    dischargeDate: string;
    los: number | null;
    disposition: string;
    drgCode: string;
  };
  cost: {
    total: number;
    nationalAvg: number | null;
    variance: number;
    isAbove: boolean;
  };
  phaseBreakdown: {
    key: string;
    label: string;
    totalCost: number;
    pctOfTotal: number;
  }[];
  serviceBreakdown: {
    label: string;
    cost: number;
  }[];
  postDischargeBreakdown: {
    label: string;
    cost: number;
    pctOfPost: number;
  }[];
  flags: string[];
  otherMeasures: {
    measureId: string;
    measureName: string;
    cost: number;
    decile: number | string;
    episodes: number | string;
  }[];
}

// ============================================
// Helper Functions
// ============================================

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}

function formatCurrencyFull(value: number): string {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function getDispositionDisplay(disposition: string): { label: string; color: string; icon: React.ReactNode } {
  const map: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
    home: { label: 'Home', color: 'bg-green-100 text-green-800', icon: <Home className="h-4 w-4" /> },
    snf: { label: 'SNF', color: 'bg-amber-100 text-amber-800', icon: <Building2 className="h-4 w-4" /> },
    homeHealth: { label: 'Home Health', color: 'bg-blue-100 text-blue-800', icon: <Home className="h-4 w-4" /> },
    rehabLtch: { label: 'Rehab/LTCH', color: 'bg-purple-100 text-purple-800', icon: <Building2 className="h-4 w-4" /> },
    readmitted: { label: 'Readmitted', color: 'bg-red-100 text-red-800', icon: <AlertTriangle className="h-4 w-4" /> },
  };
  return map[disposition] || { label: disposition, color: 'bg-gray-100 text-gray-800', icon: <Bed className="h-4 w-4" /> };
}

// Phase colors for pie chart
const PHASE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

// ============================================
// Component
// ============================================

export function MSPBPatientDetailView({
  mbi,
  practiceId,
  npi,
  onBack,
  onNavigateToMeasure,
}: MSPBPatientDetailProps) {
  const [data, setData] = useState<MSPBPatientData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPatientData();
  }, [mbi, practiceId, npi]);

  const loadPatientData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('auth_token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const url = new URL(`${apiUrl}/api/practices/${practiceId}/cost/patient/${mbi}/mspb`);
      if (npi) url.searchParams.set('npi', npi);
      
      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load patient: ${response.status}`);
      }
      
      const raw = await response.json();
      // Normalize: the API historically returned { episode, phases, interventions }
      // while this view reads { episodeDetails, cost, phaseBreakdown, flags } —
      // the mismatch crashed the render (blank page) on undefined.disposition.
      const episodeDetails = raw.episodeDetails ?? {
        admissionDate: raw.episode?.admissionDate ?? '',
        dischargeDate: raw.episode?.dischargeDate ?? '',
        los: raw.episode?.los ?? null,
        disposition: raw.episode?.disposition ?? 'home',
        drgCode: raw.episode?.drgCode ?? '',
      };
      const total = raw.cost?.total ?? raw.episode?.totalCost ?? 0;
      const nationalAvg = raw.cost?.nationalAvg ?? null;
      setData({
        mbi: raw.mbi ?? mbi,
        episodeId: raw.episodeId ?? '',
        demographics: raw.demographics ?? { dateOfBirth: '', gender: '', riskPercentile: 0 },
        episodeDetails,
        cost: raw.cost ?? {
          total,
          nationalAvg,
          variance: nationalAvg ? ((total - nationalAvg) / nationalAvg) * 100 : 0,
          isAbove: nationalAvg ? total > nationalAvg : false,
        },
        phaseBreakdown: raw.phaseBreakdown ?? raw.phases ?? [],
        serviceBreakdown: raw.serviceBreakdown ?? [],
        postDischargeBreakdown: raw.postDischargeBreakdown ?? [],
        flags: raw.flags ?? [],
        otherMeasures: raw.otherMeasures ?? [],
      });
    } catch (err) {
      console.error('Error loading MSPB patient detail:', err);
      setError('Failed to load patient details');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-2 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600">{error || 'Patient not found'}</p>
        <Button variant="outline" className="mt-4" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>
    );
  }

  const dispositionInfo = getDispositionDisplay(data.episodeDetails.disposition);
  const pieData = data.phaseBreakdown.filter(p => p.totalCost > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button variant="ghost" size="sm" onClick={onBack} className="mb-2 -ml-2">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to MSPB
        </Button>
      </div>

      {/* Patient Header Card */}
      <div className="bg-slate-900 text-white rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <User className="h-6 w-6" />
              <h2 className="text-xl font-bold">PATIENT: {data.mbi}</h2>
              <Badge className="bg-purple-600">MSPB (Hospital Episode)</Badge>
            </div>
            <div className="flex items-center gap-4 mt-2 text-slate-300 text-sm">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                DOB: {formatDate(data.demographics.dateOfBirth)}
              </span>
              <span>|</span>
              <span>Gender: {data.demographics.gender || '—'}</span>
              <span>|</span>
              <span>Risk: {data.demographics.riskPercentile}th %ile</span>
            </div>
          </div>
        </div>

        {/* Episode Journey */}
        <div className="mt-4 pt-4 border-t border-slate-700">
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-3">Episode Journey</p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="bg-blue-600 rounded-lg px-3 py-2 text-center min-w-[100px]">
                <p className="text-xs text-blue-200">Admit</p>
                <p className="font-semibold">{formatDate(data.episodeDetails.admissionDate)}</p>
              </div>
              <ArrowRight className="text-slate-500" />
            </div>
            
            <div className="flex items-center gap-2">
              <div className="bg-slate-700 rounded-lg px-3 py-2 text-center">
                <p className="text-xs text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" /> LOS</p>
                <p className="font-semibold">{data.episodeDetails.los ?? '—'} days</p>
              </div>
              <ArrowRight className="text-slate-500" />
            </div>
            
            <div className="flex items-center gap-2">
              <div className="bg-green-600 rounded-lg px-3 py-2 text-center min-w-[100px]">
                <p className="text-xs text-green-200">Discharge</p>
                <p className="font-semibold">{formatDate(data.episodeDetails.dischargeDate)}</p>
              </div>
              <ArrowRight className="text-slate-500" />
            </div>
            
            <Badge className={`${dispositionInfo.color} flex items-center gap-1`}>
              {dispositionInfo.icon}
              {dispositionInfo.label}
            </Badge>
          </div>
        </div>

        {/* Flags */}
        {data.flags.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-700">
            <div className="flex flex-wrap gap-2">
              {data.flags.map((flag, idx) => (
                <Badge key={idx} className="bg-amber-600">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {flag}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Cost Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-card border rounded-lg p-4 text-center">
          <p className="text-sm text-muted-foreground">Episode Cost</p>
          <p className="text-2xl font-bold">{formatCurrencyFull(data.cost.total)}</p>
        </div>
        <div className="bg-card border rounded-lg p-4 text-center">
          <p className="text-sm text-muted-foreground">National Avg</p>
          <p className="text-2xl font-bold">
            {data.cost.nationalAvg ? formatCurrencyFull(data.cost.nationalAvg) : '—'}
          </p>
        </div>
        <div className={`border rounded-lg p-4 text-center ${data.cost.isAbove ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
          <p className={`text-sm ${data.cost.isAbove ? 'text-red-600' : 'text-green-600'}`}>Variance</p>
          <p className={`text-2xl font-bold ${data.cost.isAbove ? 'text-red-700' : 'text-green-700'}`}>
            {data.cost.isAbove ? '+' : ''}{Math.round(data.cost.variance)}%
          </p>
        </div>
      </div>

      {/* Phase Breakdown + Service Breakdown */}
      <div className="grid grid-cols-2 gap-6">
        {/* Phase Breakdown Pie Chart */}
        <div className="bg-card border rounded-lg p-4">
          <h3 className="font-semibold mb-4 uppercase text-sm tracking-wide">Phase Breakdown</h3>
          
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="totalCost"
                  nameKey="label"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {pieData.map((_, idx) => (
                    <Cell key={idx} fill={PHASE_COLORS[idx % PHASE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value: number) => formatCurrencyFull(value)}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No phase data</p>
          )}
        </div>

        {/* Post-Discharge Breakdown */}
        <div className="bg-card border rounded-lg p-4">
          <h3 className="font-semibold mb-4 uppercase text-sm tracking-wide">Post-Discharge Breakdown</h3>
          
          {data.postDischargeBreakdown.length > 0 ? (
            <div className="space-y-3">
              {data.postDischargeBreakdown.map((item, idx) => (
                <div key={idx}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{item.label}</span>
                    <span className="text-sm font-semibold">{formatCurrencyFull(item.cost)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                      <div 
                        className="h-full bg-amber-500 rounded transition-all duration-500"
                        style={{ width: `${item.pctOfPost}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground w-10 text-right">
                      {Math.round(item.pctOfPost)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-green-600">
              <Home className="h-6 w-6 mr-2" />
              <span>Discharged to Home - No Post-Acute</span>
            </div>
          )}
        </div>
      </div>

      {/* Service Category Breakdown */}
      {data.serviceBreakdown.length > 0 && (
        <div className="bg-card border rounded-lg p-4">
          <h3 className="font-semibold mb-4 uppercase text-sm tracking-wide">Service Category Breakdown</h3>
          
          <div className="grid grid-cols-2 gap-4">
            {data.serviceBreakdown.slice(0, 8).map((svc, idx) => (
              <div key={idx} className="flex items-center justify-between border-b pb-2">
                <span className="text-sm truncate pr-2">{svc.label}</span>
                <span className="text-sm font-semibold">{formatCurrency(svc.cost)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Other Measures */}
      {data.otherMeasures.length > 0 && (
        <div className="bg-card border rounded-lg p-4">
          <h3 className="font-semibold mb-4 uppercase text-sm tracking-wide">
            Other Measures for This Patient
          </h3>
          
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">Measure</th>
                  <th className="text-right p-3 font-medium">Cost</th>
                  <th className="text-center p-3 font-medium">Decile</th>
                  <th className="text-center p-3 font-medium">Episodes</th>
                  {onNavigateToMeasure && (
                    <th className="text-center p-3 font-medium">Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.otherMeasures.map(measure => (
                  <tr key={measure.measureId} className="border-t hover:bg-muted/30">
                    <td className="p-3 font-medium">{measure.measureName}</td>
                    <td className="p-3 text-right font-mono">{formatCurrency(measure.cost)}</td>
                    <td className="p-3 text-center">{measure.decile}</td>
                    <td className="p-3 text-center">{measure.episodes}</td>
                    {onNavigateToMeasure && (
                      <td className="p-3 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onNavigateToMeasure(measure.measureId, mbi)}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default MSPBPatientDetailView;

